import classNames from 'classnames';
import * as React from 'react';
import Icon, { IconSize, IconColor, IconWeight } from 'components/styleguide/icon/Icon';

const styles = require('./Button.module.scss');

export type ButtonContext = 'talent' | 'company' | 'neutral';
export type ButtonType = 'primary' | 'secondary' | 'terceary';
export type ButtonSize = 'S' | 'M' | 'L';

interface IconOptions {
  color?: IconColor;
  size?: IconSize;
  weight?: IconWeight;
  className?: string;
}

interface ButtonProps {
  'aria-label'?: string;
  'data-testid'?: string;
  children?: React.ReactNode;
  className?: string;
  context?: ButtonContext;
  disabled?: boolean;
  href?: string;
  iconLeft?: string;
  iconLeftOptions?: IconOptions;
  iconRight?: string;
  iconRightOptions?: IconOptions;
  id?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  rel?: string;
  size?: ButtonSize;
  spinner?: boolean;
  target?: string;
  type?: ButtonType;
  typeAttr?: 'button' | 'submit' | 'reset';
}

const CONTEXT_ICON_COLOR: Record<ButtonContext, Record<ButtonType, IconColor>> = {
  talent: { primary: 'white', secondary: 'talent', terceary: 'talent' },
  company: { primary: 'white', secondary: 'company', terceary: 'company' },
  neutral: { primary: 'white', secondary: 'body', terceary: 'body' },
};

const SIZE_TO_ICON_SIZE: Record<ButtonSize, IconSize> = {
  S: 'sm',
  M: 'md',
  L: 'lg',
};

const Button: React.FC<ButtonProps> = ({
  'aria-label': ariaLabel,
  'data-testid': testId,
  children,
  className,
  context = 'neutral',
  disabled = false,
  href,
  iconLeft,
  iconLeftOptions,
  iconRight,
  iconRightOptions,
  id,
  onClick,
  onKeyDown,
  rel,
  size = 'M',
  spinner,
  target,
  type = 'primary',
  typeAttr = 'button',
}) => {
  const hasLeftIcon = !!iconLeft;
  const hasRightIcon = !!iconRight;
  const hasChildren = !!children;
  const hasSingleIcon = ((hasLeftIcon && !hasRightIcon) || (!hasLeftIcon && hasRightIcon)) && !hasChildren;

  const defaultIconColor = CONTEXT_ICON_COLOR[context][type];
  const defaultIconSize = SIZE_TO_ICON_SIZE[size];

  const renderIcon = (iconName?: string, options?: IconOptions): React.ReactNode => {
    if (!iconName) return null;

    return (
      <Icon
        name={iconName}
        color={options?.color || defaultIconColor}
        size={options?.size || defaultIconSize}
        weight={options?.weight}
        className={options?.className}
        aria-hidden="true"
      />
    );
  };

  const buttonClasses = classNames(
    styles.button,
    styles[`context-${context}`],
    styles[`type-${type}`],
    styles[`size-${size}`],
    {
      [styles.withLeftIcon]: hasLeftIcon,
      [styles.withRightIcon]: hasRightIcon,
      [styles.singleIcon]: hasSingleIcon,
    },
    className
  );

  const commonProps = {
    'aria-label': ariaLabel,
    'data-testid': testId,
    'data-context': context,
    'data-type': type,
    'data-size': size,
    'data-spinner': spinner || undefined,
    className: buttonClasses,
    id,
  };

  const buttonContent = (
    <>
      {hasLeftIcon && <span className={styles.iconLeft}>{renderIcon(iconLeft, iconLeftOptions)}</span>}
      {children && <span className={styles.text}>{children}</span>}
      {hasRightIcon && <span className={styles.iconRight}>{renderIcon(iconRight, iconRightOptions)}</span>}
    </>
  );

  if (href) {
    const linkRel = rel || (target === '_blank' ? 'noopener noreferrer' : undefined);
    if (disabled) {
      return (
        <a
          {...commonProps}
          aria-disabled="true"
          tabIndex={-1}
          onClick={(event) => event.preventDefault()}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLAnchorElement>}
        >
          {buttonContent}
        </a>
      );
    }
    return (
      <a
        href={href}
        target={target}
        rel={linkRel}
        {...commonProps}
        onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLAnchorElement>}
      >
        {buttonContent}
      </a>
    );
  }

  return (
    <button
      type={typeAttr}
      onClick={onClick}
      disabled={disabled}
      {...commonProps}
      onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLButtonElement>}
    >
      {buttonContent}
    </button>
  );
};

export default Button;
